/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import JsonQuery from 'json-query';
import { useState } from 'react';
import {
    hooks as commonHooks,
    types as commonTypes,
    utils as commonUtils,
} from '@sitebuilder/common';

import {
    getTextSchemaProperties,
    getStyleSchemaProperties,
    getLayoutSchemaProperties,
} from '../utils';
import HeaderWrapper from '../Header/HeaderWrapper';
import { Headings } from '../Header/Header';
import ExpandCollapseSectionsStyles from './ExpandCollapseSections.styles';

const CHILDREN_PLACEHOLDER_ID = 'children';

interface Section {
    title: string;
    content: string;
    isPlaceHolder: boolean;
    uniqueSectionId: string;
}

interface WidgetProperties extends commonTypes.WidgetPropertiesBase {
    sectionsList: Section[];
    defaultActiveSection: number;
    functionToTransform?: string;
    isTransformData?: boolean;
    typographyClass?: string;
    iconsType?: string;
    expandIcon?: string;
    collapseIcon?: string;
    expandIconImage?: string;
    collapseIconImage?: string;
    titles?: Headings[];
}

const ExpandCollapseSections = (props: commonTypes.WidgetInstanceProps) => {
    const { Placeholder } = commonHooks.useAppContext();
    const widgetProperties = commonHooks.useWidgetProperties<WidgetProperties>(
        props.widgetInstanceId
    );
    const {
        styles,
        sectionsList,
        defaultActiveSection,
        isTransformData,
        functionToTransform,
        iconsType,
        collapseIcon,
        expandIcon,
        expandIconImage,
        collapseIconImage,
        titles,
    } = widgetProperties || props;

    const i18n = commonHooks.useLocalization();

    const [activeSection, setActiveSection] = useState<number | null>(
        defaultActiveSection ? defaultActiveSection - 1 : null
    );

    const widgetInstance = commonHooks.useWidgetInstance(props.widgetInstanceId);
    const { data } = props;

    let isRender = {
        sections: sectionsList,
        widget: true,
        transformedData: { ...data },
    };

    const transformFunctionName = `${props.widgetInstanceId}-transform`;

    try {
        // eslint-disable-next-line no-new-func
        const transformedData =
            isTransformData &&
            commonUtils.transformDataWithGlobals(transformFunctionName, functionToTransform, {
                data,
                sectionsList,
                JsonQuery,
                i18n,
            });

        if (transformedData) {
            isRender = { ...isRender, ...transformedData };
        }
    } catch (e) {
        console.log(e);
    }

    return (
        <>
            {isRender.widget ? (
                <div
                    className={`sections-container ${
                        widgetProperties?.typographyClass ? widgetProperties.typographyClass : ''
                    }`}
                    css={css`
                        ${ExpandCollapseSectionsStyles}
                        ${getTextSchemaProperties(
                            widgetProperties,
                            data?.colorSpace
                        )}${getStyleSchemaProperties(widgetProperties)}${getLayoutSchemaProperties(
                            widgetProperties
                        )}${styles || ''}
                    `}
                    data-testid="sections-container"
                >
                    <HeaderWrapper headings={titles} data={isRender.transformedData} />
                    <div className={`section-labels`}>
                        {isRender.sections?.map((s: Section, id: number) => (
                            <>
                                <button
                                    key={id}
                                    className={`section-label label-test-${id + 1}`}
                                    aria-expanded={activeSection === id}
                                    onClick={() =>
                                        activeSection === id
                                            ? setActiveSection(null)
                                            : setActiveSection(id)
                                    }
                                    data-testid={`label-test-${id + 1}`}
                                >
                                    <p
                                        dangerouslySetInnerHTML={{
                                            __html: i18n.format(
                                                s?.title || '',
                                                isRender.transformedData
                                            ),
                                        }}
                                    />
                                    {iconsType === 'FontAwesome' ? (
                                        <i
                                            className={`icon fa ${
                                                activeSection === id ? collapseIcon : expandIcon
                                            }`}
                                            onClick={() =>
                                                activeSection === id
                                                    ? setActiveSection(null)
                                                    : setActiveSection(id)
                                            }
                                        />
                                    ) : (
                                        <img
                                            className={'icon'}
                                            src={
                                                activeSection === id
                                                    ? collapseIconImage
                                                    : expandIconImage
                                            }
                                            alt={'Left arrow'}
                                            onClick={() =>
                                                activeSection === id
                                                    ? setActiveSection(null)
                                                    : setActiveSection(id)
                                            }
                                        />
                                    )}
                                </button>
                                {activeSection === id &&
                                    (s.isPlaceHolder ? (
                                        <Placeholder
                                            key={s.uniqueSectionId}
                                            // eslint-disable-next-line max-len
                                            contentPlaceHolderId={`${CHILDREN_PLACEHOLDER_ID}-${id}`}
                                            parentWidgetInstanceId={widgetInstance?.id}
                                            data={isRender.transformedData}
                                        />
                                    ) : (
                                        <div
                                            key={id}
                                            className={`section-content content-test-${id + 1}`}
                                            data-testid={`content-test-${id + 1}`}
                                            dangerouslySetInnerHTML={{
                                                __html: i18n.format(
                                                    s?.content || '',
                                                    isRender.transformedData
                                                ),
                                            }}
                                        />
                                    ))}
                            </>
                        ))}
                    </div>
                </div>
            ) : (
                <></>
            )}
        </>
    );
};

export default ExpandCollapseSections;
